<template>
  <div>
    <Pane borderRight>
      <PaneHeader id="conversationsHeader">
        <template #title>Conversations</template>
      </PaneHeader>

      <!-- body -->
      <!-- filters -->
      <div class="p-3" id="conversationsFilters">
        <div class="d-inline-block">
          <h6 class="header-pretitle">Location</h6>
          <GroupingFilter
            :nonGroupMembers="selectActiveLocations"
            nonGroupMemberLabel="Locations"
            memberType="location"
            @update="groupingUpdate"
            :init="{}"
            :locationsOnly="true"
          />
        </div>

        <div class="px-3 mt-2 search-border">
          <StringFilter
            :isSearching="queryPending"
            testid="winback-search"
            @updateSearch="filterByString"
            @onTextChanged="onSearchTextChanged"
          />
        </div>

        <!--
          Deliberately a dropdown and not a .btn-group button: the Cypress winback
          commands select the status tabs positionally, by index within
          #conversationsFilters .btn-group, so another button group here would
          silently break all three of them.
        -->
        <div class="mt-2 d-flex align-items-center">
          <h6 class="header-pretitle mb-0 mr-2">Showing</h6>
          <b-dropdown
            size="sm"
            variant="light"
            data-testid="winback-date-window"
            :text="dateWindowLabel"
          >
            <b-dropdown-item
              v-for="option in dateWindowOptions"
              :key="String(option.value)"
              :data-testid="`winback-date-window-${option.value || 'all'}`"
              :active="dateWindowDays === option.value"
              @click="filterByDateWindow(option.value)"
            >
              {{ option.label }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div class="btn-group btn-group-sm mt-2 d-flex w-100" role="group">
          <button
            class="btn btn-light w-100"
            :class="{ active: statusFilter === 'All' }"
            data-testid="winback-tab-all"
            @click="filterByStatus('All')"
          >
            All
          </button>
          <button
            class="btn btn-light w-100"
            :class="{ active: statusFilter === 'Unresolved' }"
            data-testid="winback-tab-unresolved"
            @click="filterByStatus('Unresolved')"
          >
            Unresolved
          </button>
          <button
            class="btn btn-light w-100"
            :class="{ active: statusFilter === 'Resolved' }"
            data-testid="winback-tab-resolved"
            @click="filterByStatus('Resolved')"
          >
            Resolved
          </button>
        </div>
      </div>

      <!-- conversation list -->
      <div
        id="conversationList"
        class="px-1"
        :style="{
          height: conversationListHeight + 'px',
          overflow: 'scroll',
          'overscroll-behavior-y': 'contain'
        }"
      >
        <div v-if="conversationListHeight && !queryPending">
          <div
            v-for="conversation of conversations"
            :key="rowKey(conversation)"
            data-testid="winback-conversation-row"
            :data-conversation-id="conversation._id"
          >
            <ConversationCard :conversation="conversation" @select="selectConversation" />
            <hr class="my-1" />
          </div>
        </div>
        <div v-else>
          <div v-for="index in 7" :key="index" class="px-3">
            <b-skeleton height="150px"></b-skeleton>
          </div>
        </div>
        <div v-if="fetchMorePending" class="d-flex justify-content-center align-items-center my-3">
          <b-spinner small variant="primary" />
        </div>
        <!--
          The bound has to be visible at the point it bites. A bare "No more
          conversations" under an active window reads as an empty queue when
          older items are in fact one click away, which is the invisible
          truncation this control exists to avoid.
        -->
        <div
          v-if="selectListExhausted && conversations.length"
          class="d-flex justify-content-center align-items-center my-3 text-center px-3"
          data-testid="winback-list-exhausted"
        >
          <span v-if="dateWindowDays" data-testid="winback-list-exhausted-windowed">
            No more conversations in the last {{ dateWindowDays }} days. Choose All time to see
            older items.
          </span>
          <span v-else>No more conversations</span>
        </div>
        <div
          v-if="selectListExhausted && !conversations.length"
          class="d-flex justify-content-center align-items-center h-100"
          data-testid="winback-empty-state"
        >
          <div class="text-center">
            <i class="fas fa-inbox" style="font-size: 32px"></i>
            <div>No conversations</div>
          </div>
        </div>
      </div>
    </Pane>

    <TimelineDrawer
      v-if="selectIsMobileView"
      ref="timelineDrawer"
      @toggleDetails="$emit('toggleDetails')"
    />
  </div>
</template>

<script>
import Pane from '../Components/Pane'
import TimelineDrawer from '../Components/TimelineDrawer'
import { trackEvent } from '@/lib/analytics'
import PaneHeader from '../Components/PaneHeader'
import ConversationCard from '../Components/ConversationCard'
import CustomerStringFilter from '@/components/Modules/Customer/CustomerFilters/CustomerStringFilter'
import GroupingFilter from '@/components/Modules/Filters/GroupingFilter'
import { MIN_LIST_HEIGHT } from '../paneHeights'
import { layoutHeight } from '../layoutViewport'
import { createNamespacedHelpers } from 'vuex'

const ConversationModuleV2 = createNamespacedHelpers('conversationV2')
const LocationModule = createNamespacedHelpers('location')
const CompanyModule = createNamespacedHelpers('company')
const UserModule = createNamespacedHelpers('user')
const GroupModule = createNamespacedHelpers('group')

// Must outlast the throttle wrapped around the conversation fetch and the
// store's own short-interval guard, or an auto-continue request is swallowed
// and the list stalls exactly as it would have without one.
const AUTO_CONTINUE_DELAY_MS = 1100

export default {
  name: 'ConversationList',
  components: {
    Pane,
    PaneHeader,
    ConversationCard,
    StringFilter: CustomerStringFilter,
    GroupingFilter,
    TimelineDrawer
  },
  props: {
    queryPending: {
      type: Boolean,
      default: false
    },
    fetchMorePending: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Deep links default to the "All" tab to match WinbackView's filterless fetch.
      statusFilter: this.$route.params.conversationId ? 'All' : 'Unresolved',
      conversationListHeight: 0,
      filteredGroups: null,
      filteredGroupMembers: null,
      filteredLocations: [],
      searchString: '',
      resizeObserver: null,
      locationsToFilter: [],
      autoContinueTimer: null,
      // The status tabs below own the word All, and every other date filter in
      // the app (survey, customer, insights, reviews) says All time.
      dateWindowOptions: [
        { value: null, label: 'All time' },
        { value: 30, label: 'Last 30 days' },
        { value: 60, label: 'Last 60 days' },
        { value: 90, label: 'Last 90 days' }
      ]
    }
  },
  async mounted() {
    await this.setConversationListHeight()
    $('#conversationList')[0].addEventListener('scroll', this.evaluateScroll)
    window.addEventListener('resize', this.setConversationListHeight)
    // Not document.body: the list is part of the body's content, so observing it
    // closes a loop. The filter block varies independently of the list's height.
    const conversationsFilters = document.querySelector('#conversationsFilters')
    if (conversationsFilters) {
      this.resizeObserver = new ResizeObserver(this.setConversationListHeight)
      this.resizeObserver.observe(conversationsFilters)
    }
  },
  beforeDestroy() {
    clearTimeout(this.autoContinueTimer)
    window.removeEventListener('resize', this.setConversationListHeight)
    this.resizeObserver?.disconnect()
    $('#conversationList')?.[0]?.removeEventListener('scroll', this.evaluateScroll)
  },
  watch: {
    conversations() {
      this.scheduleAutoContinue()
    }
  },
  computed: {
    ...ConversationModuleV2.mapGetters([
      'selectConversations',
      'selectSearchedConversations',
      'selectListExhausted',
      'selectIsMobileView',
      'selectDateWindowDays'
    ]),
    ...LocationModule.mapGetters(['selectActiveLocations', 'selectDisabledLocations']),
    ...CompanyModule.mapGetters(['activeCompany']),
    ...UserModule.mapGetters(['onlineUser']),
    conversations() {
      return this.searchString ? this.selectSearchedConversations : this.selectConversations
    },
    // Read from the store, never from local state. The filter object is
    // replaced wholesale by other views (a deep link and the sidebar both call
    // updateFilters({})), which resets the window to All time. Local state would
    // go on claiming a bound the request no longer sends, so the dropdown and the
    // end-of-list copy would tell the operator the list is bounded when it is
    // not, and point them at All time when they are already on it.
    dateWindowDays() {
      return this.selectDateWindowDays ?? null
    },
    dateWindowLabel() {
      const selected = this.dateWindowOptions.find((option) => option.value === this.dateWindowDays)
      return selected ? selected.label : 'All time'
    }
  },
  methods: {
    ...ConversationModuleV2.mapActions([
      'updateFilters',
      'setActiveConversation',
      'updateIsDeeplink',
      'setSearchString'
    ]),
    ...GroupModule.mapActions(['selectGroupLocationIds']),
    // The viewport height, not document.body.clientHeight: the list is part of
    // that content, so sizing off it grows the body, which grows the list.
    async setConversationListHeight() {
      const conversationsHeader = document.querySelector('#conversationsHeader')
      const conversationsFilters = document.querySelector('#conversationsFilters')
      const navMain = document.querySelector('#nav-main')
      const headerHeight = conversationsHeader ? conversationsHeader.offsetHeight : 0
      const filtersHeight = conversationsFilters ? conversationsFilters.offsetHeight : 0
      const navHeight = this.selectIsMobileView && navMain ? navMain.offsetHeight : 0
      this.conversationListHeight = Math.max(
        MIN_LIST_HEIGHT,
        layoutHeight() - headerHeight - filtersHeight - navHeight
      )
    },
    // A page that does not fill its container emits no scroll event, so nothing
    // would ever ask for the next page. The rows the api dropped after applying
    // its limit are then stranded and the list quietly behaves as though it
    // were complete. Ask for one more page instead, and let the response say
    // whether there was one. This terminates: skip advances on every fetch, an
    // empty page sets the exhausted latch, and this refuses to run past it.
    shouldRequestMorePages(element) {
      if (!element) return false
      if (this.queryPending || this.fetchMorePending) return false
      if (this.selectListExhausted) return false
      // The comparison below only means "the rows did not fill the box" while
      // the box is the viewport-bounded scroll container. One left at
      // `height: auto` reports them equal whatever it holds.
      if (element.clientHeight < MIN_LIST_HEIGHT) return false
      if (element.clientHeight > layoutHeight()) return false
      return element.scrollHeight <= element.clientHeight
    },
    scheduleAutoContinue() {
      clearTimeout(this.autoContinueTimer)
      this.autoContinueTimer = setTimeout(() => {
        if (this.shouldRequestMorePages($('#conversationList')?.[0])) {
          this.$emit('fetchMore')
        }
      }, AUTO_CONTINUE_DELAY_MS)
    },
    evaluateScroll(event) {
      var element = event.target
      if (element.scrollHeight - element.scrollTop <= element.clientHeight + 300) {
        if (!this.fetchMorePending && !this.selectListExhausted) {
          console.log('FETCH MORE')
          this.$emit('fetchMore')
        }
      }
    },
    async groupingUpdate(val) {
      this.filteredGroups = val && val.groupIds && val.groupIds.length ? val.groupIds : null
      this.filteredGroupMembers =
        val && val.memberIds && val.memberIds.length ? val.memberIds : null
      this.filteredLocations =
        val && val.nonGroupMemberIds && val.nonGroupMemberIds.length ? val.nonGroupMemberIds : null

      let locationsToFilter = [
        ...new Set([...(this.filteredLocations || []), ...(this.filteredGroupMembers || [])])
      ]
      this.locationsToFilter = locationsToFilter.length
        ? locationsToFilter
        : this.filteredGroups?.length
        ? await this.selectGroupLocationIds(this.filteredGroups)
        : []
      this.formatAndApplyFilters()
    },
    // The api mints a fresh uuid per request for any conversation without a
    // persisted one, so keying the list on it tears every row down on each
    // refetch and destroys the scroll offset evaluateScroll reads. The list is
    // deduped by _id, so _id is stable and unique there.
    //
    // Search keeps uuid. Those results are appended without deduping and the
    // api only dedupes within a single page, so one conversation can appear
    // twice across search pages and _id would collide outright. uuid is the
    // better key there, though not a guarantee: a conversation that already has
    // a persisted uuid keeps it, so a genuine cross-page duplicate still
    // collides. That is pre-existing and is not made worse here.
    rowKey(conversation) {
      return this.searchString ? conversation.uuid : conversation._id
    },
    filterByString(text) {
      trackEvent(this.$intercom, 'Winback_Search', {
        companyId: this.activeCompany._id,
        query: text,
        userId: this.onlineUser._id
      })
      this.searchString = text
      this.formatAndApplyFilters()
    },
    filterByStatus(status) {
      if (this.statusFilter !== status) {
        this.statusFilter = status
        this.formatAndApplyFilters()
      }
    },
    filterByDateWindow(days) {
      if (this.dateWindowDays !== days) {
        this.formatAndApplyFilters({ dateWindowDays: days })
      }
    },
    formatAndApplyFilters(overrides = {}) {
      this.updateFilters({
        locationIds: this.locationsToFilter,
        search: this.searchString,
        dateWindowDays: this.dateWindowDays,
        ...overrides,
        ...(this.statusFilter === 'All'
          ? {}
          : { resolved: this.statusFilter === 'Resolved' ? true : false })
      })

      this.$emit('filtersUpdated')
    },
    selectConversation(selectedConversation) {
      const { conversationId } = this.$route.params
      if (conversationId && conversationId !== selectedConversation._id) {
        this.updateIsDeeplink(false)
      }
      this.setActiveConversation(selectedConversation)
      if (this.selectIsMobileView) {
        this.$refs.timelineDrawer.open()
      }
      trackEvent(this.$intercom, 'Message_Detail')
    },
    onSearchTextChanged(value) {
      if (value) {
        this.setSearchString(value)
      } else {
        this.setSearchString(undefined)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.search-border {
  background: white;
  border: 1px solid #d2ddec !important;
  border-radius: 6px !important;
}
.btn {
  box-shadow: none;
}
.btn-light {
  background-color: #edf2f9 !important;
  color: black;
}
.btn-light:active,
.btn-light.active,
.btn-light:hover,
.btn-light:visited {
  background-color: white !important;
  color: black;
  border-color: #d2ddec !important;
}
.btn-group > .btn.active {
  z-index: 0;
}
</style>
